/**
 * Fila local de FOTOS do checklist — o par da `answer-outbox` para evidências.
 *
 * Foto é o que mais falha no pátio: arquivo grande, sinal fraco. Antes, a foto
 * subia no instante em que era escolhida; sem rede, ela simplesmente se perdia
 * e o operador só descobria na conclusão ("foto obrigatória faltando").
 *
 * Agora a foto conferida entra na fila (IndexedDB — blob não cabe em
 * localStorage) e sai dela só quando a API confirma. Regras:
 * - A foto é do ITEM; o upload precisa do id da resposta. Enquanto a resposta
 *   ainda não chegou ao servidor, a foto espera (`waiting_answer`).
 * - Reenviar é seguro: o servidor devolve o anexo existente para a mesma foto.
 * - Recusa da regra (4xx que não é timeout/limite): sai da fila e o motivo
 *   volta para a tela. Reenviar não resolveria.
 *
 * O armazenamento é injetável: IndexedDB no navegador, memória nos testes e
 * quando o IndexedDB não existe (modo privado antigo).
 */
export interface PendingPhoto {
  /** id local (não é o id do anexo no servidor) */
  id: string;
  runId: string;
  itemId: string;
  blob: Blob;
  fileName: string;
  mimeType: string;
  queuedAt: number;
  attempts: number;
  lastError?: string | null;
}

export interface PhotoStore {
  list(runId: string): Promise<PendingPhoto[]>;
  put(p: PendingPhoto): Promise<void>;
  remove(id: string): Promise<void>;
}

export function memoryPhotoStore(): PhotoStore {
  const m = new Map<string, PendingPhoto>();
  return {
    list: async (runId) => [...m.values()].filter((p) => p.runId === runId).sort((a, b) => a.queuedAt - b.queuedAt),
    put: async (p) => void m.set(p.id, p),
    remove: async (id) => void m.delete(id),
  };
}

const DB = "bi-checklist-photos";
const STORE = "photos";

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((ok, fail) => { r.onsuccess = () => ok(r.result); r.onerror = () => fail(r.error); });
}

export function indexedDbPhotoStore(): PhotoStore | null {
  const idb = globalThis.indexedDB;
  if (!idb) return null;
  let aberto: Promise<IDBDatabase> | null = null;
  const db = () => {
    aberto ??= new Promise((ok, fail) => {
      const r = idb.open(DB, 1);
      r.onupgradeneeded = () => {
        const s = r.result.createObjectStore(STORE, { keyPath: "id" });
        s.createIndex("runId", "runId");
      };
      r.onsuccess = () => ok(r.result);
      r.onerror = () => fail(r.error);
    });
    return aberto;
  };
  const tx = async (mode: IDBTransactionMode) => (await db()).transaction(STORE, mode).objectStore(STORE);
  return {
    list: async (runId) => {
      const todos = (await req((await tx("readonly")).index("runId").getAll(runId))) as PendingPhoto[];
      return todos.sort((a, b) => a.queuedAt - b.queuedAt);
    },
    put: async (p) => void (await req((await tx("readwrite")).put(p))),
    remove: async (id) => void (await req((await tx("readwrite")).delete(id))),
  };
}

let padrao: PhotoStore | null = null;
/** IndexedDB quando existe; senão memória (a foto ainda sobe, só não sobrevive a fechar o app). */
export function defaultPhotoStore(): PhotoStore {
  padrao ??= indexedDbPhotoStore() ?? memoryPhotoStore();
  return padrao;
}

export type UploadOutcome = { ok: true } | { ok: false; retry: boolean; message: string };

export interface FlushResult {
  sent: number;
  /** fotos esperando a resposta do item chegar ao servidor */
  waiting: number;
  /** falha de rede/servidor — continuam na fila */
  failed: number;
  /** recusadas pela regra — saíram da fila; mensagens para a tela */
  rejected: string[];
}

export class PhotoOutbox {
  constructor(private readonly runId: string, private readonly store: PhotoStore = defaultPhotoStore()) {}

  list(): Promise<PendingPhoto[]> {
    return this.store.list(this.runId);
  }

  async enqueue(itemId: string, blob: Blob, fileName: string, now: number = Date.now()): Promise<PendingPhoto> {
    const p: PendingPhoto = {
      id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      runId: this.runId, itemId, blob, fileName, mimeType: blob.type || "image/jpeg",
      queuedAt: now, attempts: 0, lastError: null,
    };
    await this.store.put(p);
    return p;
  }

  discard(id: string): Promise<void> {
    return this.store.remove(id);
  }

  /**
   * Envia o que puder, em ordem. `answerIdOf` diz o id da resposta do item no
   * servidor (null = ainda não existe → a foto espera).
   */
  async flush(
    answerIdOf: (itemId: string) => string | null | undefined,
    upload: (p: PendingPhoto, answerId: string) => Promise<UploadOutcome>,
  ): Promise<FlushResult> {
    const r: FlushResult = { sent: 0, waiting: 0, failed: 0, rejected: [] };
    for (const p of await this.list()) {
      const answerId = answerIdOf(p.itemId);
      if (!answerId) { r.waiting++; continue; }
      const out = await upload(p, answerId);
      if (out.ok) {
        await this.store.remove(p.id);
        r.sent++;
      } else if (out.retry) {
        await this.store.put({ ...p, attempts: p.attempts + 1, lastError: out.message });
        r.failed++;
      } else {
        await this.store.remove(p.id);
        r.rejected.push(out.message);
      }
    }
    return r;
  }
}
